import { Link } from "react-router-dom";
import { Linkedin, Mail, ArrowRight } from "lucide-react";

const XIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.259 5.63L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
  </svg>
);
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/apiClient";
import { API_URL } from "@/_config";
import { useAuth } from "../context/AuthContext";

interface FooterSiteSettings {
  social_linkedin_url: string;
  social_twitter_url: string;
  support_mail: string;
}

const columns = [
  {
    title: "Product",
    links: [
      { label: "Pricing", to: "/pricing" },
      { label: "Product", to: "/product" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About Us", to: "/about" },
      { label: "Contact", to: "/contact" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Terms", to: "/terms" },
    ],
  },
];

export default function Footer() {
  const { isAuthenticated } = useAuth();

  const { data: settingsData } = useQuery<{ data: FooterSiteSettings }>({
    queryKey: ["site-settings-footer"],
    queryFn: () => get<FooterSiteSettings>(API_URL.SUPPORT.SITE_SETTINGS),
    staleTime: 1000 * 60 * 10,
  });
  const siteSettings = settingsData?.data;

  return (
    <footer
      style={{
        background: "linear-gradient(160deg, #0f3d0e 0%, #1a5c18 30%, #236e20 60%, #1b5a19 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          opacity: 0.04,
          backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)",
          backgroundSize: "28px 28px",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: -100,
          right: -100,
          width: 400,
          height: 400,
          background: "radial-gradient(circle, rgba(255,255,255,0.08), transparent 60%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: -80,
          left: -80,
          width: 300,
          height: 300,
          background: "radial-gradient(circle, rgba(0,0,0,0.1), transparent 60%)",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          padding: "64px 24px 0",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.4fr 1fr 1fr 1fr",
            gap: 40,
            marginBottom: 20,
          }}
          className="footer-main-grid"
        >
          <div>
            <img
              src="/images/logo.png"
              alt="LeadNova"
              style={{
                height: 120,
                marginBottom: 16,
                filter: "brightness(2)",
              }}
            />
            <p
              style={{
                fontSize: 14,
                color: "rgba(255,255,255,0.7)",
                maxWidth: 260,
                lineHeight: 1.7,
                marginBottom: 24,
              }}
            >
              AI-powered lead generation and outreach platform for modern sales teams.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              {[
                { href: siteSettings?.social_linkedin_url || "https://linkedin.com", Icon: Linkedin },
                { href: siteSettings?.social_twitter_url || "https://x.com", Icon: XIcon },
                { href: `mailto:${siteSettings?.support_mail || "info@leadnova.co"}`, Icon: Mail },
              ].map(({ href, Icon }) => (
                <a
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 10,
                    background: "rgba(255,255,255,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    transition: "all 0.2s",
                    textDecoration: "none",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.25)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.12)";
                  }}
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>

          {columns.map((col) => (
            <div key={col.title}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#fff",
                  textTransform: "uppercase",
                  letterSpacing: "1.2px",
                  marginBottom: 20,
                  paddingBottom: 10,
                  borderBottom: "2px solid rgba(255,255,255,0.15)",
                  display: "inline-block",
                }}
              >
                {col.title}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {col.links.map((link) => (
                  <Link
                    key={link.label}
                    to={link.to}
                    style={{
                      fontSize: 14,
                      color: "rgba(255,255,255,0.7)",
                      textDecoration: "none",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.color = "#fff";
                      (e.currentTarget as HTMLElement).style.paddingLeft = "4px";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.7)";
                      (e.currentTarget as HTMLElement).style.paddingLeft = "0px";
                    }}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            background: "rgba(255,255,255,0.08)",
            borderRadius: 16,
            padding: "28px 32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 40,
            border: "1px solid rgba(255,255,255,0.1)",
            backdropFilter: "blur(8px)",
            flexWrap: "wrap",
            gap: 16,
          }}
          className="footer-cta-bar"
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#fff", marginBottom: 4 }}>
              Ready to grow your pipeline?
            </div>
            <div style={{ fontSize: 14, color: "rgba(255,255,255,0.65)" }}>
              Start your 14-day free trial today. No credit card required.
            </div>
          </div>
          {!isAuthenticated && (
            <Link
              to="/pricing"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "#fff",
                color: "#1b5e1a",
                fontSize: 14,
                fontWeight: 700,
                padding: "12px 24px",
                borderRadius: 10,
                textDecoration: "none",
                transition: "all 0.2s",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              }}
            >
              Get Started Free <ArrowRight size={16} />
            </Link>
          )}
        </div>

        <div
          className="footer-bottom-bar"
          style={{
            borderTop: "1px solid rgba(255,255,255,0.15)",
            padding: "20px 0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <span style={{ fontSize: 13, color: "rgba(255,255,255,0.5)" }}>
            &copy; 2026 LeadNova. All rights reserved.
          </span>
          <div style={{ display: "flex", gap: 24 }}>
            {[
              { to: "/privacy", label: "Privacy" },
              { to: "/terms", label: "Terms" },
            ].map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                style={{
                  fontSize: 13,
                  color: "rgba(255,255,255,0.5)",
                  textDecoration: "none",
                  transition: "color 0.2s",
                }}
                onMouseEnter={(e) => ((e.target as HTMLElement).style.color = "#fff")}
                onMouseLeave={(e) => ((e.target as HTMLElement).style.color = "rgba(255,255,255,0.5)")}
              >
                {label}
              </Link>
            ))}
            <span style={{ fontSize: 13, color: "rgba(255,255,255,0.5)" }}>info@leadnova.co</span>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .footer-main-grid { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 480px) {
          .footer-main-grid { grid-template-columns: 1fr !important; }
          .footer-cta-bar { flex-direction: column !important; text-align: center !important; }
          .footer-bottom-bar { flex-direction: column !important; text-align: center !important; align-items: center !important; }
        }
      `}</style>
    </footer>
  );
}
